import { backendApi } from "./backend.api";
import { IEvent, IEventType, ParentType } from "shared/models/models";
import { getSpaceId } from "shared/utils/space";

export interface IEventListQueryParams {
  parent?: number;
  type?: ParentType | string;
  title?: number;
  spaceId?: number;
  page?: number;
  date?: string;
}

interface IPagination {
  total: number;
  pages: number;
}

export interface IEventListPagination {
  data: IEvent[];
  pagination: IPagination;
}

export interface IEventTypesQueryParams {
  spaceId?: number;
}
export const eventApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      events: build.query<IEventListPagination, IEventListQueryParams>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IEventListQueryParams = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/event",
            method: "GET",
            params: queryParams,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.data.map(({ id }) => ({
                  type: "Event" as const,
                  id,
                })),
                "Event",
              ]
            : ["Event"],
      }),
      getEvent: build.query<IEvent, number>({
        query: (id) => ({ url: `/event/${id}`, method: "GET" }),
        transformErrorResponse: (response: { route: string | number }) =>
          response.route,
        providesTags: (item) => [{ type: "Event", id: item?.id }],
      }),
      getLastTOEvent: build.query<IEvent, number>({
        query: (id) => ({ url: `/event/last-to/${id}`, method: "GET" }),
        transformErrorResponse: (response: { route: string | number }) =>
          response.route,
        providesTags: (item) => [{ type: "Event", id: item?.id }],
      }),
      getEventTypes: build.query<IEventType[], ParentType | string | undefined>(
        {
          query: (type) => {
            const spaceId = getSpaceId();
            const params: IEventTypesQueryParams = {};
            if (spaceId) {
              params.spaceId = spaceId;
            }
            return {
              url: `/event/types/${type}`,
              method: "GET",
              params: params,
            };
          },
        },
      ),
      addEvent: build.mutation<IEvent, Partial<IEvent>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IEvent> = { ...payload };
          if (spaceId) params.spaceId = spaceId;
          return {
            url: "/event",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: () => ["Event"],
        transformResponse: (response: { data: IEvent }) => response.data,
      }),
      updateEvent: build.mutation<IEvent, Partial<IEvent>>({
        query: (payload) => ({
          url: `/event/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Event", id: item?.id }],
        //transformResponse: (result, error) => [{ type: 'Visit', id: result?.id }],
      }),
      deleteEvent: build.mutation<IEvent, number>({
        query: (id) => ({
          url: `/event/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Event"],
      }),
    };
  },
});

export const {
  useEventsQuery,
  useAddEventMutation,
  useDeleteEventMutation,
  useGetEventQuery,
  useUpdateEventMutation,
  useGetEventTypesQuery,
  useGetLastTOEventQuery,
} = eventApi;
